import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.tsx';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.tsx';
import { LogoutIcon } from '@synoptic/ui-kit/icons/react/logout.tsx';
import { SettingsIcon } from '@synoptic/ui-kit/icons/react/settings.tsx';
import { UserCircleIcon } from '@synoptic/ui-kit/icons/react/user-circle.tsx';
import { useIsLaptop, useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { clsx } from 'clsx';
import { FC, useState } from 'react';
import { LogoutButton } from '@/components/logout-button.tsx';
import { MyAvatar } from '@/components/my-avatar.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { useAppLoader } from '../loader.ts';
import { useAuthInfo } from '../use-auth-info.ts';
import { DarkModeToggle } from './dark-mode-toggle.tsx';
import { GuestAuthButtons } from './guest-auth-buttons.tsx';
import { GuestAuthMenuAction } from './menu/menu-action/guest-auth-menu-action.tsx';
import { MenuActionLink } from './menu/menu-action/menu-action-link.tsx';
import { menuItemLink } from './menu/menu-action/menu-action.css.ts';
import {
  contentContainer,
  heading,
  profileMenu,
  profileMenuButton,
  profileMenuContent,
  profileMenuItem,
  profileMenuTrigger,
  username,
} from './profile-menu.css.ts';
import { useSidebarContext } from './sidebar-context.tsx';
import { sidebarGuestAuthButtons } from './sidebar.css.ts';

export const ProfileMenu: FC<{
  opened?: boolean;
}> = ({ opened = false }) => {
  const { authenticated } = useAppLoader();

  if (!authenticated) {
    return <UnauthenticatedProfileMenu />;
  }

  return <AuthenticatedProfileMenu opened={opened} />;
};

const UnauthenticatedProfileMenu = () => {
  const { isCollapsed } = useSidebarContext();
  const isLaptop = useIsLaptop();

  return isCollapsed || !isLaptop ? (
    <GuestAuthMenuAction />
  ) : (
    <GuestAuthButtons className={sidebarGuestAuthButtons} surface="sidebar" />
  );
};

const AuthenticatedProfileMenu: FC<{
  opened?: boolean;
}> = ({ opened = false }) => {
  const [isMenuShown, setIsMenuShown] = useState(opened);
  const isTablet = useIsTablet();
  const isLaptop = useIsLaptop();
  const authInfo = useAuthInfo();
  const { isCollapsed } = useSidebarContext();

  return (
    <Collapsible open={isMenuShown} onOpenChange={setIsMenuShown}>
      <div className={profileMenu}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            tabIndex={opened ? -1 : 0}
            aria-label={'Profile menu'}
            aria-expanded={isMenuShown}
            className={profileMenuTrigger}
            onClick={() => !opened && setIsMenuShown((prev) => !prev)}
          >
            <MyAvatar />
            {!isTablet || (isLaptop && !isCollapsed) ? (
              <div className={contentContainer}>
                <div className={heading}>{authInfo.fullName}</div>
                <div className={username}>@{authInfo.username}</div>
              </div>
            ) : null}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent duration={100}>
          <div className={profileMenuContent}>
            <MenuActionLink
              className={profileMenuItem}
              icon={<UserCircleIcon />}
              label="Profile"
              to={paths.makeUser(authInfo.username)}
            />
            <MenuActionLink
              className={profileMenuItem}
              icon={<SettingsIcon />}
              label="Settings"
              to={paths.settings}
            />
            {<DarkModeToggle />}
            {
              <LogoutButton
                renderTrigger={({ loading, logout }) =>
                  (!isTablet || isLaptop) && !isCollapsed ? (
                    <Button
                      loading={loading}
                      onClick={logout}
                      startIcon={<LogoutIcon />}
                      className={clsx(menuItemLink, profileMenuButton)}
                      variant="ghost"
                    >
                      Log out
                    </Button>
                  ) : (
                    <IconButton
                      loading={loading}
                      onClick={logout}
                      className={clsx(menuItemLink, profileMenuButton)}
                      variant="ghost"
                    >
                      <LogoutIcon />
                    </IconButton>
                  )
                }
              />
            }
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
